import { DependencyList, useEffect, useRef } from "react";

/** Quiet period before a filter change is allowed to hit the API. */
export const FILTER_DEBOUNCE_MS = 400;

/**
 * `useEffect` that waits for `delay` ms of quiet before running, restarting the timer on every
 * dependency change.
 *
 * Filter bars are the reason this exists. Ticking four checkboxes used to fire four requests —
 * three of which the user never sees the result of — and on the heavier pages a single "request"
 * is really several parallel calls. It also collapses the filter-change → reset-page-to-1 →
 * refetch cascade, which fired twice per edit whenever the user was not already on page 1.
 *
 * The first run is immediate: there is nothing to coalesce on mount, and delaying it would only
 * add latency to the initial page load.
 *
 * Cleanup behaves exactly like `useEffect`'s: it fires as soon as the dependencies change, not
 * when the replacement run eventually executes. That matters for the `cancelled`-flag pattern -
 * deferring it would leave an in-flight request live for the whole debounce window, free to
 * write a stale response into state (and, where the handler also sets state the effect depends
 * on, to re-trigger the effect). A run superseded before its timer elapses never happens, so it
 * never needs cleaning up.
 */
export function useDebouncedEffect(
    effect: () => void | (() => void),
    deps: DependencyList,
    delay: number = FILTER_DEBOUNCE_MS
) {
    // `effect` is a new closure every render; holding it in a ref lets the timer run the latest
    // one without the function identity itself counting as a dependency change.
    const latest = useRef(effect);
    latest.current = effect;

    const isFirstRun = useRef(true);
    const cleanup = useRef<void | (() => void)>(undefined);

    useEffect(() => {
        const run = () => {
            cleanup.current = latest.current();
        };

        let timer: ReturnType<typeof setTimeout> | undefined;
        if (isFirstRun.current) {
            isFirstRun.current = false;
            run();
        } else {
            timer = setTimeout(run, delay);
        }

        // Runs on every dependency change and on unmount, which is what makes a `cancelled`
        // flag inside the effect behave the way its author expects.
        return () => {
            if (timer) clearTimeout(timer);
            if (typeof cleanup.current === "function") cleanup.current();
            cleanup.current = undefined;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, delay]);
}
